import React from 'react';
import { Link } from 'react-router-dom';
import { resolvePublicFileUrl } from '../../utils/imageUrls';
import type { PassportEncounter } from '../../services/api/passport';
import {
  SUBJECT_TYPE_LABELS,
  encounterStatusLabel,
  encounterStatusTone,
  formatEncounterDate,
  verificationLevelLabel,
} from './passportCopy';
import {
  encounterDisplayTitle,
  type PassportFeedItem,
  type PassportFeedSection,
} from './passportFeedUtils';

const STATUS_TONE_CLASS: Record<ReturnType<typeof encounterStatusTone>, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  red: 'bg-red-500/10 text-red-300 border-red-500/25',
  neutral: 'bg-white/[0.06] text-gray-300 border-white/[0.1]',
};

type Props = {
  sections: PassportFeedSection[];
  onOpen?: (publicId: string, subjectType: string) => void;
};

function thumbUrl(encounter: PassportEncounter): string | null {
  const raw =
    encounter.subject?.image_url ||
    encounter.subject?.thumbnail_url ||
    (typeof encounter.subject?.collectible?.image_url === 'string'
      ? encounter.subject.collectible.image_url
      : null);
  if (!raw) return null;
  return resolvePublicFileUrl(raw) || raw;
}

function VehicleFeedCard({
  encounter,
  onOpen,
}: {
  encounter: PassportEncounter;
  onOpen?: Props['onOpen'];
}) {
  const img = thumbUrl(encounter);
  const tone = encounterStatusTone(encounter.status);
  const significant =
    encounter.status === 'verified' &&
    typeof encounter.subject?.rarity_stars === 'number' &&
    encounter.subject.rarity_stars >= 4;

  return (
    <Link
      to={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
      onClick={() => onOpen?.(encounter.public_id, encounter.subject_type)}
      className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-[#0c0c0c] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
        significant ? 'border-amber-500/35 shadow-[0_0_0_1px_rgba(245,158,11,0.12)]' : 'border-white/[0.08]'
      }`}
    >
      <div className="relative aspect-[16/10] bg-[#141414] overflow-hidden">
        {img ? (
          <img
            src={img}
            alt=""
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-zinc-600 text-sm">Build</div>
        )}
        {significant ? (
          <span className="absolute left-2 top-2 rounded-md border border-amber-500/30 bg-[#0a0a0a]/85 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200/90">
            Notable
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3.5">
        <p className="text-sm font-semibold text-white leading-snug line-clamp-2">
          {encounterDisplayTitle(encounter)}
        </p>
        <p className="text-xs text-zinc-500">
          Collected {formatEncounterDate(encounter.occurred_at)}
          {encounter.subject?.subtitle ? ` · ${encounter.subject.subtitle}` : ''}
        </p>
        {encounter.memory_caption ? (
          <p className="mt-1 text-xs text-zinc-400 line-clamp-2 italic">
            “{encounter.memory_caption}”
          </p>
        ) : null}
        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <span className="text-[11px] text-zinc-500">
            {verificationLevelLabel(encounter.verification_level)}
          </span>
          <span
            className={`badge shrink-0 border text-[10px] ${STATUS_TONE_CLASS[tone]}`}
            aria-label={`Status: ${encounterStatusLabel(encounter.status)}`}
          >
            {encounterStatusLabel(encounter.status)}
          </span>
        </div>
      </div>
    </Link>
  );
}

function EventFeedCard({
  encounter,
  onOpen,
}: {
  encounter: PassportEncounter;
  onOpen?: Props['onOpen'];
}) {
  const img = thumbUrl(encounter);
  return (
    <Link
      to={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
      onClick={() => onOpen?.(encounter.public_id, encounter.subject_type)}
      className="group relative flex min-h-[120px] overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0c0c0c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60 sm:col-span-2"
    >
      <div className="relative w-[38%] sm:w-[42%] shrink-0 bg-[#141414]">
        {img ? (
          <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full items-center justify-center text-zinc-600 text-xs">Event</div>
        )}
      </div>
      <div className="flex flex-1 flex-col justify-center p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
          Attended
        </p>
        <p className="mt-1 text-base font-semibold text-white leading-snug line-clamp-2">
          {encounterDisplayTitle(encounter)}
        </p>
        <p className="mt-1 text-xs text-zinc-500">
          {formatEncounterDate(encounter.occurred_at)}
          {encounter.subject?.subtitle ? ` · ${encounter.subject.subtitle}` : ''}
        </p>
      </div>
    </Link>
  );
}

function CompactFeedCard({
  encounter,
  onOpen,
}: {
  encounter: PassportEncounter;
  onOpen?: Props['onOpen'];
}) {
  const img = thumbUrl(encounter);
  const label = SUBJECT_TYPE_LABELS[encounter.subject_type] || 'Encounter';
  return (
    <Link
      to={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
      onClick={() => onOpen?.(encounter.public_id, encounter.subject_type)}
      className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#0c0c0c]/80 p-3 transition-colors hover:border-white/[0.14] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60"
    >
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-[#141414]">
        {img ? (
          <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <div className="flex h-full items-center justify-center text-[10px] text-zinc-600">
            {label.slice(0, 1)}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
        <p className="text-sm font-medium text-white truncate">{encounterDisplayTitle(encounter)}</p>
        <p className="text-xs text-zinc-500">{formatEncounterDate(encounter.occurred_at)}</p>
      </div>
    </Link>
  );
}

function VehicleBatchCard({
  item,
  onOpen,
}: {
  item: Extract<PassportFeedItem, { kind: 'vehicle_batch' }>;
  onOpen?: Props['onOpen'];
}) {
  const thumbs = item.encounters
    .map(thumbUrl)
    .filter((u): u is string => Boolean(u))
    .slice(0, 4);

  return (
    <div className="sm:col-span-2 rounded-2xl border border-white/[0.08] bg-[#0c0c0c] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
        Show day
      </p>
      <p className="mt-1 text-base font-semibold text-white">{item.title}</p>
      <p className="mt-1 text-xs text-zinc-500">{formatEncounterDate(item.encounters[0]?.occurred_at)}</p>

      {thumbs.length > 0 ? (
        <div className="mt-4 grid grid-cols-4 gap-2">
          {thumbs.map((src, i) => (
            <div key={`${src}-${i}`} className="aspect-square overflow-hidden rounded-lg bg-[#141414]">
              <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      ) : null}

      <ul className="mt-4 space-y-1.5">
        {item.encounters.map((enc) => (
          <li key={enc.public_id}>
            <Link
              to={`/passport/encounters/${encodeURIComponent(enc.public_id)}`}
              onClick={() => onOpen?.(enc.public_id, enc.subject_type)}
              className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-zinc-300 hover:bg-white/[0.04] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400/60"
            >
              <span className="truncate">{encounterDisplayTitle(enc)}</span>
              <span className="shrink-0 text-xs text-zinc-500">View</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FeedItem({ item, onOpen }: { item: PassportFeedItem; onOpen?: Props['onOpen'] }) {
  if (item.kind === 'vehicle_batch') {
    return <VehicleBatchCard item={item} onOpen={onOpen} />;
  }
  const enc = item.encounter;
  if (enc.subject_type === 'vehicle') {
    return <VehicleFeedCard encounter={enc} onOpen={onOpen} />;
  }
  if (enc.subject_type === 'event') {
    return <EventFeedCard encounter={enc} onOpen={onOpen} />;
  }
  return <CompactFeedCard encounter={enc} onOpen={onOpen} />;
}

/**
 * Infographic / collage-style chronological Passport journal.
 */
export const PassportActivityFeed: React.FC<Props> = ({ sections, onOpen }) => {
  if (sections.length === 0) return null;

  return (
    <div className="space-y-10">
      {sections.map((section) => (
        <section key={section.key} aria-labelledby={`passport-feed-${section.key}`}>
          <h3
            id={`passport-feed-${section.key}`}
            className="mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500"
          >
            {section.label}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {section.items.map((item, idx) => (
              <FeedItem
                key={
                  item.kind === 'vehicle_batch'
                    ? `batch-${item.dayKey}-${idx}`
                    : item.encounter.public_id
                }
                item={item}
                onOpen={onOpen}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default PassportActivityFeed;
